import { FC, useMemo, useState } from 'react';
import { RefreshCw, Copy, ExternalLink } from 'lucide-react';
import { Modal } from '@/components/modal';
import { Button } from '@/components/ui/button';
import { useNotification } from '@/components/notifications';
import { useOrgStore } from '@/stores/org-store';
import { useCountCollectionPoints } from '@/hooks/queries/data-sources/use-count-collection-points';
import { useGetFileTags } from '@/hooks/queries/data-sources/use-get-file-tags';
import { useReindexFile } from '@/hooks/mutations/data-sources/use-reindex-file';
import { Source } from '@/types/source-types';

interface FileInfoModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  collectionId: string;
  source: Source | null;
  canReindex: boolean;
  onReindexed?: () => void;
}

export const FileInfoModal: FC<FileInfoModalProps> = ({
  isOpen,
  onOpenChange,
  collectionId,
  source,
  canReindex,
  onReindexed,
}) => {
  const notification = useNotification();
  const orgId = useOrgStore((state) => state.currentOrg?.orgid);
  const [showConfirmReindex, setShowConfirmReindex] = useState(false);

  const filename = source?.filename;
  const externalIdFromRow = source?.tags?.external_id;

  const {
    data: tagsData,
    isLoading: isTagsLoading,
    refetch: refetchTags,
    isFetching: isTagsFetching,
  } = useGetFileTags({
    collectionId: isOpen ? collectionId : undefined,
    filename: isOpen ? filename : undefined,
    orgId,
  });

  const externalId = tagsData?.tags?.external_id || externalIdFromRow;

  const {
    data: countData,
    isLoading: isCountLoading,
    refetch: refetchCount,
    isFetching: isCountFetching,
  } = useCountCollectionPoints({
    collectionId: isOpen ? collectionId : undefined,
    externalId: isOpen ? externalId : undefined,
    orgId,
  });

  const reindexMutation = useReindexFile({
    onSuccess: (res) => {
      const msg =
        res.ingestion_method === 'celery'
          ? 'Re-indexing queued. Progress will appear in the file status column.'
          : 'Re-indexing completed.';
      notification.success(msg);
      setShowConfirmReindex(false);
      onReindexed?.();
      refetchTags();
      refetchCount();
    },
    onError: (error) => {
      notification.error(error.message || 'Failed to queue re-indexing');
    },
  });

  const title = useMemo(() => {
    const name = source?.original_filename || source?.filename || 'file';
    return `File info — ${name}`;
  }, [source]);

  const tagEntries = useMemo(() => {
    const tags = tagsData?.tags || {};
    return Object.entries(tags).sort(([a], [b]) => a.localeCompare(b));
  }, [tagsData]);

  const s3Location = useMemo(() => {
    const s3Url = source?.s3_url;
    if (!s3Url) return null;
    const match = s3Url.match(/^s3:\/\/([^/]+)\/(.+)$/);
    if (!match) return null;
    const [, bucket, key] = match;
    const consoleUrl = `https://s3.console.aws.amazon.com/s3/object/${bucket}?prefix=${encodeURIComponent(
      key,
    )}`;
    return { bucket, key, s3Url, consoleUrl };
  }, [source]);

  const handleCopy = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      notification.success(`${label} copied`);
    } catch {
      notification.error('Copy failed');
    }
  };

  const handleReindex = () => {
    if (!filename) return;
    reindexMutation.mutate({ collectionId, filename });
  };

  const handleRefresh = () => {
    refetchTags();
    refetchCount();
  };

  const isRefreshing = isTagsFetching || isCountFetching;

  return (
    <Modal
      isDialogOpen={isOpen}
      setIsDialogOpen={(open) => {
        if (!open) setShowConfirmReindex(false);
        onOpenChange(open);
      }}
      title={title}
      description='Diagnostic info for this uploaded document.'
      footer={
        <div className='flex w-full justify-between items-center'>
          <Button
            variant='ghost'
            size='sm'
            onClick={handleRefresh}
            disabled={isRefreshing}
          >
            <RefreshCw
              className={`size-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`}
            />
            Refresh
          </Button>
          <div className='flex gap-2'>
            <Button variant='ghost' onClick={() => onOpenChange(false)}>
              Close
            </Button>
            {canReindex && !showConfirmReindex && (
              <Button
                variant='default'
                onClick={() => setShowConfirmReindex(true)}
                disabled={!externalId || reindexMutation.isPending}
              >
                Force re-index
              </Button>
            )}
            {canReindex && showConfirmReindex && (
              <>
                <Button
                  variant='ghost'
                  onClick={() => setShowConfirmReindex(false)}
                  disabled={reindexMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  variant='destructive'
                  onClick={handleReindex}
                  disabled={reindexMutation.isPending}
                >
                  {reindexMutation.isPending
                    ? 'Queuing...'
                    : 'Confirm re-index'}
                </Button>
              </>
            )}
          </div>
        </div>
      }
    >
      <div className='space-y-4 text-sm'>
        <section>
          <h3 className='font-medium text-foreground mb-2'>Indexing</h3>
          <dl className='grid grid-cols-[180px_1fr] gap-y-1'>
            <dt className='text-muted-foreground'>External ID</dt>
            <dd className='font-mono break-all'>
              {externalId || (isTagsLoading ? 'Loading…' : '—')}
            </dd>
            <dt className='text-muted-foreground'>Points indexed</dt>
            <dd>
              {isCountLoading
                ? 'Loading…'
                : countData
                  ? countData.point_count.toLocaleString()
                  : '—'}
            </dd>
          </dl>
        </section>

        {s3Location && (
          <section>
            <div className='flex items-center justify-between mb-2'>
              <h3 className='font-medium text-foreground'>Storage</h3>
              <a
                href={s3Location.consoleUrl}
                target='_blank'
                rel='noopener noreferrer'
                className='inline-flex items-center gap-1 text-xs text-primary hover:underline'
              >
                Open in S3 Console
                <ExternalLink className='size-3' />
              </a>
            </div>
            <dl className='grid grid-cols-[180px_1fr] gap-y-1'>
              <dt className='text-muted-foreground'>Bucket</dt>
              <dd className='font-mono break-all'>{s3Location.bucket}</dd>
              <dt className='text-muted-foreground'>Key</dt>
              <dd className='font-mono break-all flex items-start gap-2'>
                <span className='flex-1'>{s3Location.key}</span>
                <Button
                  variant='ghost'
                  size='icon'
                  className='size-6 shrink-0'
                  onClick={() => handleCopy(s3Location.key, 'S3 key')}
                  aria-label='Copy S3 key'
                >
                  <Copy className='size-3' />
                </Button>
              </dd>
              <dt className='text-muted-foreground'>S3 URI</dt>
              <dd className='font-mono break-all flex items-start gap-2'>
                <span className='flex-1'>{s3Location.s3Url}</span>
                <Button
                  variant='ghost'
                  size='icon'
                  className='size-6 shrink-0'
                  onClick={() => handleCopy(s3Location.s3Url, 'S3 URI')}
                  aria-label='Copy S3 URI'
                >
                  <Copy className='size-3' />
                </Button>
              </dd>
            </dl>
          </section>
        )}

        <section>
          <h3 className='font-medium text-foreground mb-2'>S3 tags</h3>
          {isTagsLoading ? (
            <p className='text-muted-foreground'>Loading…</p>
          ) : tagEntries.length === 0 ? (
            <p className='text-muted-foreground'>No tags found.</p>
          ) : (
            <dl className='grid grid-cols-[180px_1fr] gap-y-1'>
              {tagEntries.map(([key, value]) => (
                <div key={key} className='contents'>
                  <dt className='text-muted-foreground'>{key}</dt>
                  <dd className='font-mono break-all'>{value}</dd>
                </div>
              ))}
            </dl>
          )}
        </section>

        {canReindex && showConfirmReindex && (
          <div className='rounded-md border border-destructive/30 bg-destructive/5 p-3 text-destructive'>
            Re-indexing will delete the existing Qdrant points for this file and
            re-queue ingestion. Any edited field overrides made after upload
            will be lost. Continue?
          </div>
        )}
      </div>
    </Modal>
  );
};
